import { useRef, useState } from "react";
import { ClipboardCheck, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errorMessage } from "@/lib/api";

const initialItems = (lead) =>
  lead.visit_checklist || (lead.ai_analysis?.questions_for_visit || []).map((text) => ({ text, checked: false, note: "" }));

const Item = ({ item, index, onChange, onRemove, onBlurSave }) => (
  <li className="rounded-xl border border-border bg-card p-3" data-testid={`checklist-item-${index}`}>
    <label className="flex cursor-pointer items-start gap-3">
      <Checkbox checked={item.checked} onCheckedChange={(v) => onChange({ ...item, checked: !!v }, true)}
        className="mt-0.5 h-6 w-6 rounded-md data-[state=checked]:border-[color:var(--brand)] data-[state=checked]:bg-[color:var(--brand)]"
        data-testid={`checklist-checkbox-${index}`} />
      <span className={`flex-1 text-[15px] leading-snug ${item.checked ? "text-muted-foreground line-through" : "text-foreground"}`}>{item.text}</span>
      <button type="button" onClick={(e) => { e.preventDefault(); onRemove(); }} aria-label="Remove item" data-testid={`checklist-remove-${index}`}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground"><Trash2 className="h-4 w-4" /></button>
    </label>
    <input value={item.note} placeholder="Answer / note" maxLength={1000} data-testid={`checklist-note-${index}`}
      onChange={(e) => onChange({ ...item, note: e.target.value }, false)} onBlur={onBlurSave}
      className="mt-2 h-11 w-full rounded-lg border border-border bg-muted px-3 text-sm outline-none focus:border-[color:var(--brand)]" />
  </li>
);

export const VisitChecklist = ({ lead }) => {
  const [items, setItems] = useState(() => initialItems(lead));
  const [draft, setDraft] = useState("");
  const latest = useRef(items);
  latest.current = items;

  const save = async (next) => {
    try { await api.put(`/leads/${lead.id}/checklist`, { items: next }); }
    catch (e) { toast.error(errorMessage(e, "Couldn't save checklist")); }
  };
  const update = (next, persist) => { setItems(next); if (persist) save(next); };
  const add = (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    update([...latest.current, { text: draft.trim(), checked: false, note: "" }], true);
    setDraft("");
  };
  const done = items.filter((i) => i.checked).length;

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-soft" data-testid="visit-checklist">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground"><ClipboardCheck className="h-4 w-4 text-muted-foreground" />Visit checklist</h2>
        <span className="text-sm font-semibold text-muted-foreground" data-testid="checklist-progress">{done}/{items.length}</span>
      </div>
      <ul className="mt-3 space-y-2">
        {items.map((it, i) => (
          <Item key={i} item={it} index={i}
            onChange={(v, persist) => update(latest.current.map((x, j) => (j === i ? v : x)), persist)}
            onRemove={() => update(latest.current.filter((_, j) => j !== i), true)}
            onBlurSave={() => save(latest.current)} />
        ))}
      </ul>
      <form onSubmit={add} className="mt-3 flex gap-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Add your own item" maxLength={300} data-testid="checklist-add-input"
          className="h-12 flex-1 rounded-xl border-2 border-border bg-card px-3 text-[15px] outline-none focus:border-[color:var(--brand)]" />
        <button type="submit" aria-label="Add item" data-testid="checklist-add-button"
          className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-900 text-white"><Plus className="h-5 w-5" /></button>
      </form>
    </div>
  );
};
